import { Card } from "@general-backoffice/core";
import { FormInput } from "@general-backoffice/core/index";
import moment from "moment";
import React from "react";
import { useNavigate } from "react-router";
import { Badge, Card as RSCard, CardBody, FormGroup } from "reactstrap";

import MonthlyEarningsGraph from "./graphs/MonthlyEarningsGraph";

const STARTING_YEAR = 2023,
  FINAL_YEAR = parseInt(moment().format("YYYY"), 10);
const years: number[] = [];
for (let y = STARTING_YEAR; y <= FINAL_YEAR; y++) years.push(y);

interface MonthlyEarningsData {
  totalCharged?: number;
  totalPending?: number;
  totalPaidOut?: number;
  perMonth?: Record<string, { charged?: number; pending?: number; paidOut?: number }>;
}

interface MonthlyEarningsGraphCardProps {
  yearDataMap?: Record<number, MonthlyEarningsData>;
  selectedYear?: number;
  isLoading?: boolean;
  setYear?: (year: number) => void;
}

const MonthlyEarningsGraphCard = ({
  yearDataMap = {},
  selectedYear,
  isLoading,
  setYear,
}: MonthlyEarningsGraphCardProps) => {
  const navigate = useNavigate();
  const selectedYearData = yearDataMap?.[selectedYear ?? 0] ?? {};
  const { totalCharged = 0, totalPending = 0, totalPaidOut = 0 } = selectedYearData;

  const perMonthMap = Object.fromEntries(
    Object.entries(yearDataMap ?? {}).map(([y, d]) => [Number(y), d.perMonth ?? {}])
  );

  return (
    <RSCard>
      <Card.Header title="Monthly earnings">
        <FormGroup className="mb--4" style={{ minWidth: 120 }}>
          <FormInput.Select
            placeholder="Select a year"
            onChange={(opt: { value?: number }) => {
              if (opt.value) setYear?.(opt.value);
            }}
            value={{ value: selectedYear, label: selectedYear }}
            options={years.map((y) => ({ value: y, label: y }))}
            isDisabled={isLoading}
            isClearable={false}
          />
        </FormGroup>
      </Card.Header>
      <CardBody className="pb-4">
        <div className="position-relative w-100 mb-4">
          <div className="position-absolute d-flex top-0 left-0 w-100" style={{ transform: "translateY(-50%)" }}>
            <Badge color="default" children={`Charged: ${totalCharged?.toFixed(2)} €`} />
            <Badge
              color="danger"
              style={{ cursor: "pointer" }}
              title="See users with missing payments"
              onClick={() => navigate("/billing/missing-payments")}
              children={`Pending: ${totalPending?.toFixed(2)} €`}
            />
            <Badge color="warning" children={`Paid out: ${totalPaidOut?.toFixed(2)} €`} />
          </div>
        </div>

        <MonthlyEarningsGraph
          yearDataMap={perMonthMap}
          isLoading={isLoading}
          className="rounded-bottom overflow-hidden"
          style={{ height: "calc(100vh - 400px)", minHeight: 400 }}
        />
      </CardBody>
    </RSCard>
  );
};

export default MonthlyEarningsGraphCard;
